import { useCallback, useEffect, useState } from 'react'
import { CalendarPlus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchWindows,
  createWindow,
  cancelWindow,
  deleteWindow,
  toRFC3339,
  MW_STATUS,
  MW_STR as S,
  type MaintWindow,
} from './maintenanceApi'

export function MaintenanceScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<MaintWindow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchWindows(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const schedule = async () => {
    const s = toRFC3339(from)
    const e = toRFC3339(to)
    if (!title.trim() || !s || !e) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createWindow(title.trim(), message.trim(), s, e)
      setTitle('')
      setMessage('')
      setFrom('')
      setTo('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const cancel = async (id: number) => {
    try {
      await cancelWindow(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteWindow(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A24.21" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Schedule form */}
          <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={S.name}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={S.message}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-tertiary">
                {S.from}
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="mt-1 block rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                />
              </label>
              <label className="text-xs text-tertiary">
                {S.to}
                <input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="mt-1 block rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                />
              </label>
              <button
                type="button"
                onClick={schedule}
                disabled={busy || !title.trim() || !from || !to}
                className="flex items-center gap-1 rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                <CalendarPlus size={14} />
                {S.schedule}
              </button>
            </div>
          </div>

          {/* Windows list */}
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((w) => {
                const st = MW_STATUS[w.status] ?? { label: w.status, pill: 'bg-surface-3 text-tertiary' }
                return (
                  <div key={w.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm text-primary">{w.title}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${st.pill}`}>{st.label}</span>
                        </div>
                        {w.message && <p className="mt-1 text-sm text-secondary">{w.message}</p>}
                        <p className="mt-0.5 text-xs text-tertiary">
                          {new Date(w.starts_at).toLocaleString()} → {new Date(w.ends_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1.5">
                        {(w.status === 'scheduled' || w.status === 'active') && (
                          <button type="button" onClick={() => cancel(w.id)} className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary">
                            {S.cancel}
                          </button>
                        )}
                        <button type="button" onClick={() => del(w.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
